import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * authStore.ts
 * Holds the authenticated user's identity and roles.
 * Populated by useCurrentUser hook after MSAL login completes.
 */

export interface AuthState {
  email: string
  alias: string
  displayName: string
  roles: string[]
  tenant: string
  /** All tenant values from user records (one record per tenant) */
  availableTenants: string[]
  /** Roles keyed by tenant — populated when user has records in multiple tenants */
  tenantRoles: Record<string, string[]>
  isAuthenticated: boolean
}

interface AuthActions {
  setUser: (user: Omit<AuthState, 'isAuthenticated'>) => void
  /** Update active tenant and switch roles to those of the new tenant */
  setTenant: (tenant: string) => void
  clearUser: () => void
}

const initialState: AuthState = {
  email: '',
  alias: '',
  displayName: '',
  roles: [],
  tenant: '',
  availableTenants: [],
  tenantRoles: {},
  isAuthenticated: false,
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,
      setUser: (user) => set({ ...user, isAuthenticated: true }),
      setTenant: (tenant) => set((s) => ({
        ...s,
        tenant,
        roles: s.tenantRoles[tenant] ?? s.roles,
      })),
      clearUser: () => set(initialState),
    }),
    {
      name: 'du-auth',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
)
